/**
 * TimeRangeSelector - Reusable time period selector
 * Can be used for filtering data by time range
 */

import React from 'react';
import { Calendar } from 'lucide-react';

export interface TimeRange {
  value: string;
  label: string;
}

export interface TimeRangeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  ranges?: TimeRange[];
  label?: string;
  showIcon?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const defaultRanges: TimeRange[] = [
  { value: '1d', label: '1 Day' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '3 Months' },
  { value: '180d', label: '6 Months' },
  { value: 'ytd', label: 'YTD' },
  { value: '1y', label: '1 Year' },
  { value: '3y', label: '3 Years' },
  { value: 'all', label: 'All Time' }
];

const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  value,
  onChange,
  ranges = defaultRanges,
  label,
  showIcon = false,
  className = '',
  size = 'md'
}) => {
  const sizeConfig = {
    sm: 'px-2 py-1 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-2.5 text-base'
  };

  const sizeClass = sizeConfig[size];

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        {showIcon && (
          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
        )}
        <select
          value={value}
          onChange={handleChange}
          className={`
            ${sizeClass}
            ${showIcon ? 'pl-10' : ''}
            pr-3
            block w-full
            border border-gray-300 
            rounded-md 
            text-gray-700 
            bg-white
            focus:outline-none 
            focus:ring-2 
            focus:ring-blue-500 
            focus:border-blue-500
            appearance-none
            cursor-pointer
            hover:border-gray-400
            transition-colors
          `}
        >
          {ranges.map(range => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default TimeRangeSelector;